import { legendContent } from "../../components/BrowserView/Maps/Widgets/legendContent";
import { disclaimerContent } from "../../components/BrowserView/Maps/Widgets/disclaimerContent";
import { customSymbolRenderer } from "./customSymbolRenderer";

import Graphic from "@arcgis/core/Graphic";
import Expand from "@arcgis/core/widgets/Expand";
import Slider from "@arcgis/core/widgets/Slider";
import Locate from "@arcgis/core/widgets/Locate";
import Search from "@arcgis/core/widgets/Search.js";
import TileLayer from "@arcgis/core/layers/TileLayer";
import LayerList from "@arcgis/core/widgets/LayerList";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery.js";

export const addPhotoLocationsLayer = (map, setPhotoLocationsLayer) => {
 const photoLocationsLayer = new FeatureLayer({
  url: "https://services3.arcgis.com/Pn95KPPz613AKHXi/ArcGIS/rest/services/Photo360Locations/FeatureServer",
  outFields: ["*"],
  renderer: customSymbolRenderer,
  legendEnabled: false,
  popupEnabled: true,
 });
 map.add(photoLocationsLayer);
 setPhotoLocationsLayer(photoLocationsLayer);
};

export const addStormScenarioLayers = (map, stormScenarios) => {
 stormScenarios.forEach((scenario) => {
  if (scenario.tileURL?.length) {
   const tileLayer = new TileLayer({
    url: scenario.tileURL,
    opacity: 0.4,
    visible: false,
    name: "Flooding Depth",
    popupEnabled: false,
   });
   map.add(tileLayer);
  }
 });
};

export const addCriticalFacilitiesLayers = (
 map,
 criticalFacilitiesAssetLayers,
 setCriticalFacilitiesLayers
) => {
 criticalFacilitiesAssetLayers.forEach((item) => {
  const newCriticalFacilityLayer = new FeatureLayer({
   url: item.url,
   visible: false,
   legendEnabled: true,
   listMode: "hide",
   popupEnabled: true,
  });
  map.add(newCriticalFacilityLayer);
  setCriticalFacilitiesLayers((oldState) => ({
   ...oldState,
   [item.stateVar]: newCriticalFacilityLayer,
  }));
 });
};

export const addWidgets = (
 mapView,
 checked,
 legendDivID,
 currSelectionID,
 isMap1
) => {
 // create new basmap gallery widget
 let basemapGallery = new BasemapGallery({
  view: mapView,
 });
 // add Basemap Gallery
 mapView.ui.add(
  new Expand({
   content: basemapGallery,
   expanded: false,
   expandTooltip: "Change Basemap",
   view: mapView,
  }),
  "top-right"
 );

 // create and add Disclaimer Widget
 if (!!isMap1) {
  mapView.ui.add(
   new Expand({
    content: disclaimerContent,
    expanded: !checked,
    expandTooltip: "Toggle Layers",
    view: mapView,
   }),
   "bottom-right"
  );
 }

 // add a legend container to the map
 let legendDiv = document.createElement("div");
 legendDiv.id = `${legendDivID}`;
 legendDiv.innerHTML = legendContent;

 // add a custom legend to the map
 mapView.ui.add(
  new Expand({
   content: legendDiv,
   expanded: !!checked ? false : true,
   expandTooltip: "Legend",
   view: mapView,
  }),
  "bottom-left"
 );

 // add a current selection box to the map
 let div = document.createElement("div");
 div.id = `${currSelectionID}`;
 div.innerHTML = `<div> Adaptation / SLR / Rainfall / Surge / Groundwater </div>`;
 mapView.ui.add(div, "bottom-left");

 // create and add Locate Widget
 mapView.ui.add(
  new Locate({
   view: mapView,
   graphic: new Graphic({
    symbol: {
     type: "simple-marker",
     color: "#10a0fa",
     outline: {
      color: "#fffff",
      width: 1,
     },
    },
   }),
   goToLocationEnabled: true,
   goToOverride: (view, options) => {
    options.target.scale = 1500;
    return view.goTo(options.target);
   },
  }),
  "bottom-right"
 );
};

// update the current selection text
export const generateSelectionText = (reductionState, slr, rainfall) => {
 const adaptationText =
  reductionState["selectedAdaptation"] === "None"
   ? "No Adaptation"
   : reductionState["selectedAdaptation"] || "Adaptation";

 const slrText = slr ? (slr.includes("SLR") ? slr : `${slr} SLR`) : "SLR";

 const rainfallText = rainfall ? `${rainfall} Rainfall` : "Rainfall";

 const stormSurgeText =
  reductionState["selectedStormSurge"] === "No Surge"
   ? "No Surge"
   : reductionState["selectedStormSurge"] || "Surge";

 const groundwaterText =
  reductionState["selectedGroundwaterConditions"] || "Groundwater";

 return `${adaptationText} / ${slrText} / ${rainfallText} / ${stormSurgeText} / ${groundwaterText}`;
};

// update the current selection text
export const updateCurrSelection = (currSelectionBox, selectionText) => {
 if (currSelectionBox.length) {
  currSelectionBox.forEach((domnode) => {
   domnode.innerHTML = null;
  });
  currSelectionBox[0].innerHTML = `<div>${selectionText}</div>`;
 }
};

// update the legend content
export const updateLegendDiv = (legendDivBox, legendContent) => {
 if (legendDivBox.length) {
  legendDivBox.forEach((domNode) => {
   domNode.innerHTML = null; // Clear existing content
  });
  legendDivBox[0].innerHTML = legendContent; // Set new content
 }
};

export const handleLayerVisibility = (
 item,
 resultantArray,
 floodRenderingsVisible
) => {
 // If the resultant array includes the scenario name and its length is 1
 if (resultantArray.includes(item.title) && resultantArray.length === 1) {
  item.visible = true; // Make the item visible
 } else {
  item.visible = false; // Hide all other layers
 }

 // If the layer is a 360 photo layer, show it if flood renderings are visible
 if (item.title.includes("360") && floodRenderingsVisible) {
  item.visible = true;
 }
};

export const initializeLayerList = async (
 mapView,
 resultantArray,
 floodRenderingsVisible,
 setPhotoLocationsLayerView
) => {
 const layerList = new LayerList({
  view: mapView,
  listItemCreatedFunction: async (event) => {
   const item = event.item;

   // Wait for the layer to load
   await item.layer.when();

   // If the layer is a 360 photo layer, set the global layer variable
   if (item.layer.title === "Photo360Locations") {
    mapView.whenLayerView(item.layer).then((layerView) => {
     setPhotoLocationsLayerView(layerView);
    });
   }

   // Handle layer visibility based on the resultant array and flood renderings visibility
   handleLayerVisibility(item, resultantArray, floodRenderingsVisible);
  },
 });
};

export const filterScenarios = (scenarioOne, stormScenarios) => {
 return stormScenarios.filter((scenario) => {
  if (
   scenarioOne?.rainfall &&
   scenarioOne?.slr &&
   scenarioOne?.surge &&
   scenarioOne?.groundwater &&
   scenarioOne?.adaptation
  ) {
   return (
    scenario.rainfallAmount === scenarioOne.rainfall &&
    scenario.SLR === scenarioOne.slr &&
    scenario.stormSurge === scenarioOne.surge &&
    scenario.groudwaterConditions === scenarioOne.groundwater &&
    scenario.adaptationScenario === scenarioOne.adaptation
   );
  } else if (
   scenarioOne?.rainfall &&
   scenarioOne?.slr &&
   scenarioOne?.surge &&
   scenarioOne?.groundwater
  ) {
   return (
    scenario.rainfallAmount === scenarioOne.rainfall &&
    scenario.SLR === scenarioOne.slr &&
    scenario.stormSurge === scenarioOne.surge &&
    scenario.groudwaterConditions === scenarioOne.groundwater
   );
  } else if (scenarioOne?.rainfall && scenarioOne?.slr && scenarioOne?.surge) {
   return (
    scenario.rainfallAmount === scenarioOne.rainfall &&
    scenario.SLR === scenarioOne.slr &&
    scenario.stormSurge === scenarioOne.surge
   );
  }
  return null;
 });
};

export const constructGallery = (
 locationTitle,
 filteredScenarios,
 photoLocations
) => {
 const gallery = [];
 const baselineURL = photoLocations[locationTitle]?.baseline?.url;
 if (baselineURL) gallery.push(baselineURL);

 filteredScenarios.forEach((scenario) => {
  const scenarioUrl =
   photoLocations[locationTitle]?.scenario?.[scenario.title]?.url;
  if (scenarioUrl) gallery.push(scenarioUrl);
 });

 return gallery;
};

export const createSlider = (mapView) => {
 //  create a container for the slider and slider title
 const sliderDivContainer = document.createElement("div");
 const sliderDiv = document.createElement("div");
 const sliderDivTitle = document.createElement("div");
 sliderDivContainer.classList.add("slider-div");
 sliderDivTitle.innerHTML = "Flooding Visibility";
 sliderDivContainer.append(sliderDivTitle); // append the title container
 sliderDivContainer.append(sliderDiv); // append the slider container
 // opacity slider widget:
 const slider = new Slider({
  container: sliderDiv,
  min: 0,
  max: 1,
  precision: 2,
  values: [0.4],
  visibleElements: {
   // labels: true,
   rangeLabels: true,
  },
 });
 // customize range labels
 slider.labelFormatFunction = function (value, type) {
  return type === "min" ? `0%` : type === "max" ? `100%` : value;
 };
 mapView.ui.add(sliderDivContainer, "top-right");

 return slider;
};

export const createLayerList = (mapView, slider, globalReductionState) => {
 const layerList = new LayerList({
  view: mapView,
  listItemCreatedFunction: (event) => {
   const itemView = event.item;
   itemView.hidden = true;

   itemView.watch("visible", () => {
    layerList.operationalItems.forEach((layerView) => {
     if (
      layerView.title === globalReductionState?.finalSelection[0]?.["title"]
     ) {
      layerView.hidden = false;
      layerView.layer.opacity = slider.values[0];

      reactiveUtils.watch(
       () => slider.values.map((value) => value),
       (values) => (layerView.layer.opacity = values[0])
      );
     } else {
      layerView.hidden = true;
     }
    });
   });
  },
 });

 //  mapView.ui.add(layerList, "top-right");
};

export const createSearchWidget = (mapView, setSearchResultsFeature) => {
 const searchWidget = new Search({
  view: mapView,
  popupEnabled: false,
  resultGraphicEnabled: false,
  goToLocationEnabled: true,
  container: "searchWidget",
 });

 searchWidget.on("search-clear", () => {
  mapView.graphics.removeAll();
  setSearchResultsFeature(null);
 });

 searchWidget.on("search-complete", (event) => {
  const searchResults = event.results;
  searchResults.forEach((res) => {
   const feature = res.results[0].feature;
   feature.symbol = {
    type: "simple-marker",
    color: "#10a0fa",
    size: 13.5,
    outline: {
     color: "#fffff",
     width: 2,
    },
   };
   setSearchResultsFeature(feature);
   mapView.graphics.add(feature);
   mapView.goTo(res.results[0].target).catch((err) => console.error(err));
  });
 });

 //  mapView.ui.add(searchWidget, "top-right");

 return searchWidget;
};

export const updateResultantArray = (
 condition,
 data,
 setResultantArray,
 isAdaptation
) => {
 if (condition && data) {
  const titles = data.map((item) => item.title);
  setResultantArray(titles);
 } else {
  if (!!isAdaptation) setResultantArray([]);
 }
};
